import clsx from 'clsx'
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion'
import { useCallback, useRef, useState } from 'preact/hooks'

import { Interactions, trackingService } from '../../machines/tracking.machine'
import { useNaturalPowerContext } from '../NaturalPowerCalculatorProvider'
import { useStepSlider } from './Context'
import { getNearestStep } from './utils'

interface ThumbProps {
  timeConstant?: number
  className?: string
  name?: string
}

export function Thumb({ className, timeConstant = 50 }: ThumbProps) {
  const services = useNaturalPowerContext()
  const [naturalPowerRatio, setNaturalPowerRatio] = services.naturalPower

  const [isDragging, setIsDragging] = useState(false)
  const { steps, constraintRef, thumbRef, trackLength } = useStepSlider()
  const changeRef = useRef<() => void>()

  const modifyTarget = useCallback(
    (v: number) => (Math.round((v / trackLength) * steps) / steps) * trackLength,
    [steps, trackLength],
  )
  const x = useMotionValue(naturalPowerRatio * trackLength)

  if (!isDragging) {
    x.set(naturalPowerRatio * trackLength)
  }

  const clearChangeRef = () => {
    if (changeRef.current) {
      changeRef.current()
      changeRef.current = undefined
    }
  }

  const snap = useTransform(x, modifyTarget)
  const spring = useSpring(snap, { stiffness: 900, damping: 45 })

  const handleDragStart = useCallback(() => {
    setIsDragging(true)
    document.querySelector('body .syn-npc')?.firstElementChild?.classList.add('select-none')
    document.querySelector('body')?.classList.add('overflow-clip')
    document.querySelector('body')?.classList.add('sm:overflow-visible')
    clearChangeRef()
    changeRef.current = snap.onChange((position: number) => {
      const ratio = getNearestStep(position, trackLength, steps) / steps
      setNaturalPowerRatio(ratio)
      services.costInput.send({ type: 'Update inputs', naturalPowerRatio: ratio })
    })
  }, [snap, trackLength, steps, setNaturalPowerRatio, services.costInput])

  const handleDragEnd = useCallback(() => {
    setIsDragging(false)
    document.querySelector('body .syn-npc')?.firstElementChild?.classList.remove('select-none')
    document.querySelector('body')?.classList.remove('overflow-clip')
    document.querySelector('body')?.classList.remove('sm:overflow-visible')
    trackingService.send({ type: 'Interact', name: Interactions.contributionSlider })
    clearChangeRef()
  }, [])

  return (
    <div className={clsx('w-full')}>
      <motion.div
        className={clsx(
          'absolute rounded-full pointer-events-none bg-yellow/10 ring-2 md:ring ring-white/25',
          className,
        )}
        style={{ x: isDragging ? spring : snap }}
      />
      <motion.div
        ref={thumbRef}
        drag="x"
        style={{ x }}
        dragConstraints={constraintRef}
        dragElastic={false}
        dragMomentum={false}
        dragTransition={{
          modifyTarget,
          timeConstant,
        }}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        whileHover={{ scale: 1.02 }}
        className={clsx(
          'absolute transition-shadow duration-300 rounded-full cursor-pointer bg-yellow ring-2 md:ring ring-white/80 hover:ring-white hover:shadow-xl',
          className,
        )}
      />
    </div>
  )
}
